/**
 * @codex PR #785 round 2, F2 — PRESENCE-bit detection for the
 * `properties/apply` undo inverse.
 *
 * ## The defect this exists for
 *
 * The live `properties/apply` inverse (`editorStore.ts` →
 * `buildPropertiesApplyInverseFromSnapshots`) is a DISPLAY-STRING replay: it
 * snapshots the prior value of every preview key the apply changed and replays
 * those strings forward through the typed writer. A display string can carry a
 * value, but it cannot carry the difference between *absent* and *present and
 * equal to the default that absence implies*.
 *
 * On the DXF wire that difference is real data. The heavy POLYLINE's group
 * 40/41 defaults are what every VERTEX without its own 40/41 inherits, so
 * whether the group EXISTS is itself information — which is exactly why the
 * writer (`dxf-io/writer.ts`, `if (g.defaultStartWidth !== undefined)`) is
 * presence-preserving rather than "emit when non-zero". Editing the width of an
 * imported polyline that omitted group 40 and then undoing therefore replayed
 * the DISPLAYED inherited `"0"` and left an EXPLICIT zero behind: the entity
 * did not return to its pre-edit state, and the next export emitted a group the
 * file never had. History is expected to restore the pre-state exactly, so this
 * is a defect, not a documentable limitation of the display-string inverse.
 *
 * ## Why a PRESENCE detector rather than an absence sentinel
 *
 * The alternative shape — an "this was absent" sentinel string smuggled through
 * the property bag — has to be spelled out per field, in the overlay, by hand.
 * The presence-bit problem has already appeared three times in one PR
 * (per-vertex widths, entity-level defaults, the compact-store carry), so a
 * mechanism that needs a human to remember a new optional WILL silently lose
 * the next one. This detector instead reads the PRESENCE BITS STRUCTURALLY off
 * the typed pre/post document entities, so every optional a schema ever grows —
 * at any depth, including per-vertex ones — is covered the day it is added,
 * with no list to maintain. When it fires, the caller composes the exact
 * pre-edit TYPED SNAPSHOT into the inverse, which restores the whole entity
 * verbatim rather than re-deriving any single field.
 *
 * Scope: the ABSENT → PRESENT direction only, which is the one a typed-writer
 * `properties/apply` can produce (the plain writer sets fields, it never
 * deletes one; the coupled/side-effect fields that CAN vanish belong to the
 * viewport snapshot sweep that already restores them). Reporting the opposite
 * direction here would fire on every edit of a compact-store-backed entity —
 * a separate carry defect — and turn a targeted composition into an
 * always-on one.
 */

/** Own, non-inherited plain-data object (a schema-parsed entity or sub-object). */
function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}

/**
 * The first field path that is ABSENT on `prev` and PRESENT on `next`, or
 * `null` when `next` introduced no field `prev` lacked.
 *
 * Absence is `undefined` — the schema parse leaves an omitted optional either
 * missing or explicitly `undefined`, and both mean "this DXF group was not
 * written", so they are one case here.
 *
 * Arrays are walked ELEMENT-WISE over the indices both sides share, so a vertex
 * that GAINED its own width override is reported (`vertices.3.startWidth`)
 * while a longer or shorter list — a structural edit, not a presence bit — is
 * not. Returns on the FIRST hit: the caller only needs the yes/no, and this
 * runs per touched entity on every history-recording apply (a grip drag commit
 * included), so it must not walk a 10 000-vertex polyline to completion once
 * the answer is already known.
 */
export function firstGainedOptionalFieldPath(
  prev: unknown,
  next: unknown,
  path = "",
): string | null {
  if (Array.isArray(prev) && Array.isArray(next)) {
    const shared = Math.min(prev.length, next.length);
    for (let i = 0; i < shared; i += 1) {
      const hit = firstGainedOptionalFieldPath(prev[i], next[i], `${path}${i}.`);
      if (hit !== null) return hit;
    }
    return null;
  }
  if (!isPlainRecord(prev) || !isPlainRecord(next)) return null;
  for (const key of Object.keys(next)) {
    const nextValue = next[key];
    if (nextValue === undefined) continue;
    if (prev[key] === undefined) return `${path}${key}`;
    const hit = firstGainedOptionalFieldPath(
      prev[key],
      nextValue,
      `${path}${key}.`,
    );
    if (hit !== null) return hit;
  }
  return null;
}
